import { observer } from 'mobx-react-lite'
import { Navigate, Route, useLocation } from 'react-router'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { BillingRoutes, BillingPath, PersonalRoutes } from 'entities/ExportRouters'
import { RoutesWithNotFound } from 'entities/ExportRouters/ui/RoutesWithNotFound'
import { IntegrationsRouter, IntegrationsRoutes } from 'pages/settings/pages/integrations'
import { SettingsProvider } from 'pages/settings/context/settingsContext'
import { QuickLinksPage } from 'pages/settings/pages/quickLinks'
import { ComplianceRouter, ComplianceRoutes } from 'pages/settings/pages/compliance'
import { NumbersRouter, NumbersRoutes } from 'pages/settings/pages/numbers'
import { DoubleOptInRouter, DoubleOptInRoutes } from 'pages/settings/pages/doubleOptIn'
import { ReferralsRouter, ReferralsRoutes } from 'pages/settings/pages/referrals'
import { EmailToSmsRouter, EmailToSmsRoutes } from 'pages/settings/pages/emailToSms'
import { InboxesRouter, InboxesRoutes } from 'pages/settings/pages/inboxes'
import { MembersRouter, MembersRoutes } from 'pages/settings/pages/members'
import { CustomFieldsRouter, CustomFieldsRoutes } from 'pages/settings/pages/customFields'
import { SettingsLayout } from 'pages/settings/layout/SettingsLayout'
import { InboxesRouterV1 } from 'pages/settings/pages/inboxes/route/InboxesRouterV1'
import { RcsRoutes } from 'pages/settings/pages/rcs/route/type'
import { RcsRouter } from 'pages/settings/pages/rcs/route/RcsRouter'
import { TeamsRouter } from 'pages/settings/pages/teams/route/TeamsRouter'
import { TeamsAccessLayout } from 'pages/settings/pages/teams/layout/TeamsAccessLayout'
import { TeamsRoutes } from 'pages/settings/pages/teams/route/type'
import { SavedRepliesRoutes } from 'pages/settings/pages/savedReplies/route/type'
import { SavedRepliesRouter } from 'pages/settings/pages/savedReplies/route/SavedRepliesRouter'
import { BillingRouter } from 'pages/settings/pages/billing/route/billingRouter'
import { AddOnsRouter, AddOnsRoutes } from 'pages/settings/pages/addOns'
import { DeveloperRouter, DeveloperRoutes } from 'pages/settings/pages/developer'
import { PersonalRouter } from 'pages/settings/pages/personal'
import { AddSubscriptionRouter } from 'pages/settings/pages/billing/route/addSubscriptionRouter'
import { RolesPermissionsAccessLayout } from 'pages/settings/pages/rolesPermissions/layout/RolesPermissionsAccessLayout'
import { RolesPermissionsPage } from 'pages/settings/pages/rolesPermissions'
import { TagsRouter } from 'pages/settings/pages/tags/route/tagsRouter'
import { SettingsRoutes } from 'pages/settings/route/type'
import { NotificationsRouter, NotificationsRoutes } from 'pages/settings/pages/notifications'
import { WidgetChatRouter, WidgetChatRoutes } from 'pages/settings/pages/widgetChat'
import { urlShortenerRoutes } from 'pages/settings/pages/urlShortener/route/type'
import { UrlShortenerRouter } from 'pages/settings/pages/urlShortener/route/urlShortenerRouter'
import { MembersPath } from 'pages/settings/pages/members/route/membersPath'

const SettingsRoutesContent = observer(() => {
  const { subscriptionStore } = useEntitiesContext()
  const { isCanceled } = subscriptionStore
  const { pathname } = useLocation()

  if (isCanceled && pathname === BillingPath.overview) {
    return <Navigate to='/settings/personal/settings/profile' replace />
  }

  return (
    <RoutesWithNotFound>
      <Route element={<SettingsLayout />}>
        <Route index element={<div>Settings Home</div>} />
        <Route path={`${ComplianceRoutes.root}/*`} element={<ComplianceRouter />} />
        <Route path={`${IntegrationsRoutes.root}/*`} element={<IntegrationsRouter />} />
        <Route path={`${NumbersRoutes.root}/*`} element={<NumbersRouter />} />
        <Route path={`${SettingsRoutes.quickLinks}`} element={<QuickLinksPage />} />
        <Route path={`${RcsRoutes.root}/*`} element={<RcsRouter />} />
        <Route path={`${TeamsRoutes.root}/*`} element={<TeamsAccessLayout />}>
          <Route path='*' element={<TeamsRouter />} />
        </Route>
        <Route path={`${SavedRepliesRoutes.root}/*`} element={<SavedRepliesRouter />} />
        <Route path={`${SettingsRoutes.organization}`}>
          <Route path={`${DoubleOptInRoutes.root}/*`} element={<DoubleOptInRouter />} />
          <Route path={`${EmailToSmsRoutes.root}/*`} element={<EmailToSmsRouter />} />
          <Route path={`${ReferralsRoutes.root}/*`} element={<ReferralsRouter />} />
          {/* InboxesRouterV1 can't be deleted during using calling */}
          {/* settings/organization/inboxes/all-inboxes/id/calling */}
          <Route path={`${InboxesRoutes.root}/*`} element={<InboxesRouterV1 />} />
          <Route path={`${AddOnsRoutes.root}/*`} element={<AddOnsRouter />} />
          <Route path={`${urlShortenerRoutes.root}/*`} element={<UrlShortenerRouter />} />
          <Route
            path={`${MembersRoutes.root}/access-requested`}
            element={<Navigate to={`/${MembersPath.permissionsUpdate}`} replace />}
          />
        </Route>
        <Route path={`${SettingsRoutes.tags}/*`} element={<TagsRouter />} />
        <Route element={<RolesPermissionsAccessLayout />}>
          <Route path={`${SettingsRoutes.rolesPermissions}`} element={<RolesPermissionsPage />} />
        </Route>
        <Route path={`${InboxesRoutes.root}/*`} element={<InboxesRouter />} />
        <Route path={`${MembersRoutes.root}/*`} element={<MembersRouter />} />
        <Route path={`${CustomFieldsRoutes.root}/*`} element={<CustomFieldsRouter />} />

        <Route path={`${BillingRoutes.upgrade}`} element={<AddSubscriptionRouter />} />
        <Route path={`${BillingRoutes.root}/*`} element={<BillingRouter />} />

        <Route path={`${DeveloperRoutes.root}/*`} element={<DeveloperRouter />} />

        <Route path={`${PersonalRoutes.root}/*`} element={<PersonalRouter />} />
        <Route path={`${NotificationsRoutes.root}/*`} element={<NotificationsRouter />} />
        <Route path={`${WidgetChatRoutes.root}/*`} element={<WidgetChatRouter />} />
      </Route>
    </RoutesWithNotFound>
  )
})

export const SettingsRouter = () => {
  return (
    <SettingsProvider>
      <SettingsRoutesContent />
    </SettingsProvider>
  )
}
